import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { useUpdateAPITool, useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';

import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IAPIError } from '@/modules/gpt-chats/types/tools.service.type';
import { Switch } from '@/components/ui-kit/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { Info } from 'lucide-react';
import { useAuthStore } from '@/state/store/auth';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { useTranslation } from 'react-i18next';

interface EditToolModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: {
    id: string;
    name: string;
    url: string;
    description: string;
    status?: string;
  };
  isBlocksTool: boolean;
  copiedBlocksTool: boolean;
}

interface EditToolFormData {
  toolName: string;
  url: string;
  description: string;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  status: boolean;
}

export const EditToolModal = ({
  open,
  onOpenChange,
  tool,
  isBlocksTool,
  copiedBlocksTool,
}: EditToolModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.memberships?.some((m) => m?.roles?.includes('admin'));
  const { mutateAsync: updateAPITool, isPending } = useUpdateAPITool();

  const { data: toolDetails } = useGetToolDetails(tool?.id || '', isBlocksTool, open);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    control,
    watch,
  } = useForm<EditToolFormData>({
    defaultValues: {
      toolName: '',
      url: '',
      description: '',
      isOpenTool: false,
      isBlocksAuthenticated: false,
      status: false,
    },
  });

  useEffect(() => {
    if (open) {
      if (toolDetails) {
        reset(
          {
            toolName: toolDetails.Name,
            description: toolDetails.Description || '',
            url: toolDetails.APIConfig?.BaseURL || '',
            isOpenTool: toolDetails.IsOpenTool || false,
            isBlocksAuthenticated: toolDetails.IsBlocksAuthenticated || false,
            status: toolDetails.Status === 'active',
          },
          { keepDirtyValues: true }
        );
      } else if (tool) {
        reset(
          {
            toolName: tool.name,
            description: tool.description,
            url: tool.url,
            isOpenTool: isBlocksTool || false,
            isBlocksAuthenticated: false,
            status: tool.status === 'active',
          },
          { keepDirtyValues: true }
        );
      }
    }
  }, [open, toolDetails, tool, reset, isBlocksTool]);

  const isBlocksToolChecked = watch('isOpenTool');

  const onSubmit = async (data: EditToolFormData) => {
    if (!tool?.id) {
      showErrorToast({ errors: t('TOOL_ID_NOT_FOUND') });
      return;
    }

    try {
      const response = await updateAPITool({
        tool_id: tool.id,
        name: data.toolName,
        description: data.description || '',
        base_url: data.url,
        is_open_tool: isAdmin ? data.isOpenTool : undefined,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : undefined,
        status: isAdmin && data.isOpenTool ? (data.status ? 'active' : 'inactive') : undefined,
      });

      if (response.is_success) {
        showSuccessToast({ description: response.detail || t('API_TOOL_UPDATED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['tools'] });
        handleCancel();
      } else {
        showErrorToast({ errors: response.message || t('FAILED_TO_UPDATE_API_TOOL') });
      }
    } catch (error: unknown) {
      const apiError = error as IAPIError;
      if (apiError?.detail && Array.isArray(apiError.detail)) {
        const errorMessages = apiError.detail.map((err) => err.msg).join(', ');
        showErrorToast({ errors: errorMessages });
      } else {
        showErrorToast({ errors: ErrorTransformer(error) });
      }
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{t('EDIT_TOOL')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">{t('EDIT_TOOL_DESCRIPTION')}</p>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <div className="flex gap-6">
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <label htmlFor="toolName" className="text-sm font-medium">
                  {t('TOOL_NAME_LABEL')} <span className="text-destructive">*</span>
                </label>
                <Input
                  id="toolName"
                  placeholder={t('ENTER_NAME')}
                  {...register('toolName', {
                    required: t('TOOL_NAME_REQUIRED'),
                  })}
                  className={errors.toolName ? 'border-destructive' : ''}
                />
                {errors.toolName && (
                  <p className="text-sm text-destructive">{errors.toolName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="url" className="text-sm font-medium">
                  {t('BASE_URL_LABEL')} <span className="text-destructive">*</span>
                </label>
                <Input
                  id="url"
                  placeholder={t('ENTER_BASE_URL')}
                  {...register('url', {
                    required: t('BASE_URL_REQUIRED'),
                    validate: (value) => {
                      if (!value.startsWith('http://') && !value.startsWith('https://')) {
                        return t('URL_MUST_START_WITH_HTTP');
                      }
                      return true;
                    },
                  })}
                  onBlur={() => trigger('url')}
                  className={errors.url ? 'border-destructive' : ''}
                />
                {errors.url && <p className="text-sm text-destructive">{errors.url.message}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  {t('DESCRIPTION')} <span className="text-destructive">*</span>
                </label>
                <Textarea
                  id="description"
                  placeholder={t('TOOL_DESCRIPTION_PLACEHOLDER')}
                  rows={4}
                  {...register('description', {
                    required: t('DESCRIPTION_IS_REQUIRED'),
                  })}
                  className={errors.description ? 'border-destructive' : ''}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>

              {isBlocksToolChecked && (
                <div className="pointer-events-none flex items-center gap-2">
                  <Controller
                    name="isOpenTool"
                    control={control}
                    render={({ field }) => (
                      <Checkbox id="editIsOpenTool" checked={!!field.value} disabled />
                    )}
                  />
                  <label
                    htmlFor="editIsOpenTool"
                    className="cursor-not-allowed text-sm font-medium text-muted-foreground"
                  >
                    {t('BLOCKS_TOOL_LABEL')}
                  </label>
                </div>
              )}

              {isAdmin && isBlocksToolChecked && !copiedBlocksTool && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pt-2">
                    <Controller
                      name="isBlocksAuthenticated"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="isBlocksAuthenticated"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    <label
                      htmlFor="isBlocksAuthenticated"
                      className="cursor-pointer text-sm font-medium"
                    >
                      {t('ALLOW_TOOL_AUTHENTICATION')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ALLOW_AUTH_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="status"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                    <label htmlFor="status" className="text-sm font-medium">
                      {t('TOOL_VISIBILITY')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('TOGGLE_TOOL_STATUS_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t('CANCEL')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending || !isValid}>
            {isPending ? t('SAVING') : t('SAVE')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
